<script setup lang="ts">
import { twMerge } from "tailwind-merge";
import { computed } from "vue";
import { type ControlSize, control } from "@/design/control";
import Field from "./Field.vue";

/**
 * Um texto de várias linhas, no mesmo envelope e com a mesma receita de borda.
 *
 * A altura é o único ponto em que ele foge do `control.ts`: quem manda é `rows`,
 * então a altura fixa sai e sobra o mesmo respiro vertical do tamanho pedido —
 * um textarea de uma linha mede exatamente o que um `TextField` do mesmo `size`
 * mede.
 */
defineOptions({ inheritAttrs: false });

const props = defineProps<{
	label?: string;
	modelValue: string;
	rows?: number;
	size?: ControlSize;
	placeholder?: string;
	hint?: string;
	error?: string;
	/** Inválido sem mensagem própria — o erro está sendo desenhado por fora. */
	invalid?: boolean;
	describedBy?: string;
	id?: string;
	required?: boolean;
	disabled?: boolean;
	class?: string;
}>();
const emit = defineEmits<{ "update:modelValue": [string] }>();

const classes = computed(() =>
	twMerge(control({ size: props.size }), "h-auto py-1.5", props.class),
);
</script>

<template>
	<Field
		v-slot="{ id: controlId, describedBy: describes, invalid: wrong }"
		:label="label"
		:hint="hint"
		:error="error"
		:required="required"
		:invalid="invalid"
		:described-by="describedBy"
		:id="id"
	>
		<textarea
			:id="controlId"
			:value="modelValue"
			:rows="rows ?? 4"
			:placeholder="placeholder"
			:required="required"
			:disabled="disabled"
			:aria-invalid="wrong"
			:aria-describedby="describes"
			:class="classes"
			v-bind="$attrs"
			@input="emit('update:modelValue', ($event.target as HTMLTextAreaElement).value)"
		/>
	</Field>
</template>
